import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SMS_CREDENTIALS_QUERY_KEY } from 'constants/QueryKeys.constants';
import { useApi } from 'contexts/ApiContext';

import type { HTTPError } from 'api/error';
import type {
  SmsCredentialsRequest,
  SmsCredentialsResponse,
} from 'api/resources/smsCredentials/types';
import { curryPostFunction } from 'api/utils';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import useProjectRoute from 'hooks/useProjectRoute';
import useSampleProject from 'hooks/useSampleProjects/useSampleProject';
import { useToast } from 'hooks/useToast';
import { useIsPlgProd } from '../useIsPlgProd';

const DEFAULT_SMS_INTEGRATION = 'twilio';

const useSmsCredentials = (
  enabled: boolean = true,
  integration: string = DEFAULT_SMS_INTEGRATION,
) => {
  const api = useApi();
  const { accountId, projectId } = useProjectRoute();
  const { inSampleProject } = useSampleProject();
  const { isProjectResourceEnabled } = useProjectResource();
  const isTwilioDisabled = useIsPlgProd();
  const isEnabled =
    enabled &&
    isProjectResourceEnabled(PROJECT_RESOURCE.SMS) &&
    !isTwilioDisabled;

  const toast = useToast();

  const queryKey = [accountId, projectId, SMS_CREDENTIALS_QUERY_KEY];

  const queryClient = useQueryClient();

  const {
    data: smsCredentials,
    isLoading: isLoadingSmsCredentials,
    error: smsCredentialsError,
  } = useQuery(
    queryKey,
    () => api.methods.getSmsCredentials(accountId, projectId, integration),
    {
      enabled: isEnabled && !inSampleProject,
      retry: false,
    },
  );

  const { mutate: upsertSmsCredentials, isLoading: isUpsertingSmsCredentials } =
    useMutation<
      SmsCredentialsResponse,
      HTTPError,
      { request: SmsCredentialsRequest; isEdit: boolean }
    >(
      curryPostFunction(api.methods.upsertSmsCredentials)({
        accountId,
        projectId,
        integration,
      }),
      {
        retry: false,
        mutationKey: queryKey,
        onSuccess: (data, args) => {
          queryClient.setQueryData(queryKey, data);

          if (args?.isEdit) {
            toast.success({
              title: 'Successfully updated SMS credentials',
            });
          } else {
            toast.success({
              title: 'Successfully added SMS credentials',
            });
          }
        },
        onError: (_, args) => {
          if (args?.isEdit) {
            toast.error({
              title: 'Failed to update SMS credentials',
            });
          } else {
            toast.error({
              title: 'Failed to add SMS credentials',
            });
          }
        },
      },
    );

  return useMemo(
    () => ({
      smsCredentials,
      isLoadingSmsCredentials: isEnabled && isLoadingSmsCredentials,
      smsCredentialsError,
      upsertSmsCredentials,
      isUpsertingSmsCredentials,
    }),
    [
      isEnabled,
      smsCredentials,
      isLoadingSmsCredentials,
      smsCredentialsError,
      upsertSmsCredentials,
      isUpsertingSmsCredentials,
    ],
  );
};

export default useSmsCredentials;
